// Shared client for the Financial Report service.
//
// The ONLY place in the frontend that reads authoritative monthly / all-time
// financial totals. Never compute revenue, payroll, Grace, or net locally —
// call one of these helpers so every surface stays reconciled.
import { supabase } from "@/integrations/supabase/client";

export type PeriodType = "month" | "all_time" | "custom";

export interface FinancialReportRequest {
  period_type: PeriodType;
  month?: string;          // "YYYY-MM"
  start_date?: string;     // "YYYY-MM-DD"
  end_date?: string;       // "YYYY-MM-DD"
  include_historical?: boolean;
}

export interface FinancialPeriod {
  label: string;
  period_type: PeriodType;
  start_date: string;
  end_date: string;
  cutoff: string;
}

export interface MonthlyReport {
  ok: true;
  period: FinancialPeriod;
  summary: {
    revenue_collected_cents: number;
    refund_cents: number;
    net_revenue_cents: number;
    payroll_paid_cents: number;
    payroll_projected_cents: number;
    total_payroll_cents: number;
    grace_percent: number;
    grace_accrued_cents: number;
    grace_paid_cents: number;
    net_operating_cents: number;
    paid_registration_count: number;
  };
  detail: {
    revenue: { included: any[]; excluded: any[] };
    payroll_paid: any[];
    payroll_projected: any[];
    grace_payments: any[];
  };
  needs_review: any[];
}

export interface AllTimeReport {
  ok: true;
  period: FinancialPeriod;
  summary: {
    revenue_collected_cents: number;
    refund_cents: number;
    net_revenue_cents: number;
    payroll_paid_cents: number;
    outstanding_projected_payroll_cents: number;
    grace_percent: number;
    grace_paid_cents: number;
    grace_accrued_unpaid_cents: number;
    net_operating_cents: number;
    paid_registration_count: number;
    revenue_generating_class_count: number;
  };
  detail: MonthlyReport["detail"];
  needs_review: any[];
}

export interface MonthlyHistoryRow {
  month: string;
  label: string;
  revenue_cents: number;
  refund_cents: number;
  net_revenue_cents: number;
  payroll_paid_cents: number;
  payroll_projected_cents: number;
  grace_accrued_cents: number;
  net_operating_cents: number;
  source_counts: {
    revenue_rows: number;
    payroll_paid_rows: number;
    payroll_projected_rows: number;
  };
}

async function invoke<T>(action: string, body: Record<string, unknown> = {}): Promise<T> {
  const { data, error } = await supabase.functions.invoke("financial-report", {
    body: { action, ...body },
  });
  if (error) throw error;
  if ((data as any)?.ok === false) throw new Error((data as any).error ?? "Financial report failed");
  return data as T;
}

export function fetchMonthlyReport(req: FinancialReportRequest): Promise<MonthlyReport> {
  return invoke<MonthlyReport>("financial_report", { ...req });
}

export function fetchAllTimeReport(includeHistorical = false): Promise<AllTimeReport> {
  return invoke<AllTimeReport>("all_time", { include_historical: includeHistorical });
}

export function fetchMonthlyHistory(includeHistorical = false) {
  return invoke<{ ok: true; cutoff: string; months: MonthlyHistoryRow[] }>("monthly_history", {
    include_historical: includeHistorical,
  });
}

export function money(cents: number | null | undefined): string {
  const n = Number(cents) || 0;
  const abs = Math.abs(n) / 100;
  const s = abs.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${n < 0 ? "-" : ""}$${s}`;
}

/** Build the list of selectable months from cutoff → this month. */
export function buildMonthOptions(cutoffISO: string): Array<{ value: string; label: string }> {
  const out: Array<{ value: string; label: string }> = [];
  const cutoff = new Date(`${cutoffISO}T00:00:00Z`);
  const now = new Date();
  let y = cutoff.getUTCFullYear();
  let m = cutoff.getUTCMonth() + 1;
  const endY = now.getUTCFullYear();
  const endM = now.getUTCMonth() + 1;
  while (y < endY || (y === endY && m <= endM)) {
    const key = `${y}-${String(m).padStart(2, "0")}`;
    const label = new Date(Date.UTC(y, m - 1, 1)).toLocaleString("en-US", {
      month: "long", year: "numeric", timeZone: "UTC",
    });
    out.push({ value: key, label });
    m += 1; if (m > 12) { m = 1; y += 1; }
  }
  return out.reverse();
}
